// components/CurrencySelector.tsx
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectCurrencyAvailable, selectCurrencyStatus, selectSelectedCurrency } from '../redux/selectors';
import { setCurrency } from '../redux/slices/currencySlice';
import { Select, MenuItem } from '@mui/material';
import i18n from '../services/localization/i18n';

// Currency codes are (mostly) an ISO-3166 country code + a letter, so we can derive the
// flag for the common cases; anything not covered just renders without a flag.
const CURRENCY_TO_COUNTRY: Record<string, string> = {
    EUR: 'EU'
};

function getCurrencyFlag(code: string): string {
    const countryCode = CURRENCY_TO_COUNTRY[code] ?? code.slice(0, 2);
    if (!/^[A-Z]{2}$/.test(countryCode)) return '';
    try {
        const first = 127397 + countryCode.charCodeAt(0);
        const second = 127397 + countryCode.charCodeAt(1);
        return String.fromCodePoint(first, second);
    } catch {
        return '';
    }
}

export function CurrencySelector() {
    const dispatch = useAppDispatch();
    const selected = useAppSelector(selectSelectedCurrency);
    const available = useAppSelector(selectCurrencyAvailable);
    const status = useAppSelector(selectCurrencyStatus);

    if (status === 'loading') {
        return <span style={{ fontSize: 12, color: '#aaa' }}>loading...</span>;
    }

    const lang = (i18n.language || 'uz') as 'en' | 'uz' | 'ru' | 'tr';
    const nameKey = `name_${lang}` as keyof (typeof available)[number];
    return (
        <Select
            value={selected}
            onChange={(e) => dispatch(setCurrency(e.target.value as any))}
            size="small"
            sx={{
                fontSize: 12,
                '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                }
            }}
        >
            {available.map((c) => {
                const name = (c[nameKey] ?? c.name_en ?? c.name_uz ?? c.name_ru ?? c.code) as string;
                const flag = getCurrencyFlag(c.code);
                return (
                    <MenuItem key={c.code} value={c.code}>
                        {(flag ? flag + ' ' : '') + c.code + ' \u2014 ' + name}
                    </MenuItem>
                );
            })}
        </Select>
    );
}
